import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bookmark, ArrowUpRight, Heart, Users } from "lucide-react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { countryLabel } from "../lib/countries";
import { tEnum, pickLang, startupPath } from "../lib/format";
import { useCurrency } from "../lib/currency";
import StartupCover from "./StartupCover";

export default function StartupCard({ s, onBookmark, onIntro, isBookmarked, canAct = false }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const { fmtShort } = useCurrency();
  return (
    <Card className="card-hover overflow-hidden flex flex-col bg-card" data-testid="startup-card">
      <Link to={startupPath(s)} className="block" data-testid="startup-card-view-link">
        <div className="aspect-[16/9] w-full overflow-hidden bg-secondary">
          <StartupCover s={s} imgClassName="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.03]" />
        </div>
      </Link>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <Link to={startupPath(s)} className="font-display font-semibold text-lg leading-snug hover:text-[hsl(var(--primary))] transition-colors block truncate">
              {s.name}
            </Link>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <Badge variant="secondary" className="bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]">{tEnum(t, "sectors", s.sector)}</Badge>
              <Badge variant="outline">{tEnum(t, "stages", s.stage)}</Badge>
              <span className="text-xs text-muted-foreground">· {countryLabel(s.country, lang)}</span>
            </div>
          </div>
        </div>
        <p className="text-sm text-foreground/80 line-clamp-2">{pickLang(s.tagline, lang)}</p>
        {pickLang(s.traction, lang) && (
          <p className="text-xs text-muted-foreground line-clamp-1">{pickLang(s.traction, lang)}</p>
        )}
        <div className="mt-auto pt-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span><span className="font-medium text-foreground">{fmtShort(s.funding_ask)}</span> {t("dealflow.ask_suffix")}</span>
            {s.likes > 0 && <span className="inline-flex items-center gap-1"><Heart className="h-3 w-3" />{s.likes}</span>}
            {s.followers > 0 && <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{s.followers}</span>}
          </div>
          <div className="flex items-center gap-1">
            {canAct && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onBookmark?.(s)}
                  data-testid="startup-card-bookmark-button"
                  className={isBookmarked ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}
                  aria-label={t("dealflow.bookmark")}
                >
                  <Bookmark className={`h-4 w-4 ${isBookmarked ? "fill-current" : ""}`} />
                </Button>
                <Button type="button" size="sm" onClick={() => onIntro?.(s)} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="startup-card-request-intro-button">
                  {t("dealflow.request_intro")}
                </Button>
              </>
            )}
            {!canAct && (
              <Button asChild variant="ghost" size="sm" className="text-[hsl(var(--primary))]">
                <Link to={startupPath(s)} className="inline-flex items-center gap-1">
                  {t("dealflow.view")} <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
